import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const MetadataForm = ({ projectId, isGlobal = false, initialData = null, onSave = null }) => {
    const [metadata, setMetadata] = useState(initialData || {
        practice_name: '',
        practice_address: '',
        practice_phone: '',
        practice_email: '',
        practice_website: '',
        nhs_org_code: '',
        cqc_registration: '',
        partners_list: [],
        salaried_gps: [],
        practice_manager: { name: '', contact: '' },
        nursing_staff: [],
        admin_staff: [],
        opening_hours: {},
        emergency_contact: '',
        out_of_hours_provider: '',
        affiliated_services: [],
        additional_info: '',
        custom_fields: {}
    });
    const [loading, setLoading] = useState(true);

    const fetchMetadata = useCallback(async () => {
        if (isGlobal) {
            if (initialData) setMetadata(prev => ({ ...prev, ...initialData }));
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(`/api/projects/${projectId}/metadata`);
            // Merge with default state to ensure all fields exist
            setMetadata(prev => ({ ...prev, ...data }));
        } catch (error) {
            toast.error('Failed to load practice details');
        } finally {
            setLoading(false);
        }
    }, [projectId, isGlobal, initialData]);

    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isGlobal && onSave) {
                await onSave(metadata);
            } else {
                await api.post(`/api/projects/${projectId}/metadata`, metadata);
                toast.success('Practice details saved');
            }
        } catch (error) {
            if (!isGlobal) toast.error('Failed to save practice details');
        }
    };

    const handleChange = (field, value) => {
        setMetadata(prev => ({ ...prev, [field]: value }));
    };

    if (loading) return <div>Loading practice details...</div>;

    return (
        <div className="metadata-form">
            <h2>Practice Information</h2>
            <p className="description">These details are used to automatically populate merged policies.</p>

            <form onSubmit={handleSave}>
                <section className="form-section">
                    <h3>Essential Details</h3>
                    <div className="form-group">
                        <label>Practice Name</label>
                        <input
                            type="text"
                            value={metadata.practice_name || ''}
                            onChange={(e) => handleChange('practice_name', e.target.value)}
                            placeholder="e.g., Greenfield Medical Centre"
                        />
                    </div>
                    <div className="form-group">
                        <label>Practice Address</label>
                        <textarea
                            value={metadata.practice_address || ''}
                            onChange={(e) => handleChange('practice_address', e.target.value)}
                            placeholder="Full address include postcode"
                        />
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Phone Number</label>
                            <input
                                type="text"
                                value={metadata.practice_phone || ''}
                                onChange={(e) => handleChange('practice_phone', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={metadata.practice_email || ''}
                                onChange={(e) => handleChange('practice_email', e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <section className="form-section">
                    <h3>Registration</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>NHS Org Code (ODS)</label>
                            <input
                                type="text"
                                value={metadata.nhs_org_code || ''}
                                onChange={(e) => handleChange('nhs_org_code', e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label>CQC Registration</label>
                            <input
                                type="text"
                                value={metadata.cqc_registration || ''}
                                onChange={(e) => handleChange('cqc_registration', e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                <section className="form-section">
                    <h3>Additional Details</h3>
                    <div className="form-group">
                        <label>Additional Information / Ad-hoc Details</label>
                        <p className="description__small">
                            Enter any specific instructions, temporary changes, or ad-hoc details that should supersede standard policy text (e.g., "The Practice Manager is currently on leave, refer queries to Deputy").
                        </p>
                        <textarea
                            className="input--large"
                            value={metadata.additional_info || ''}
                            onChange={(e) => handleChange('additional_info', e.target.value)}
                            placeholder="e.g. Specific projected hours, temporary staffing arrangements, or emphasis on specific local procedures."
                        />
                    </div>
                </section>

                {/* Note: In a full version, we'd add complex editors for staff lists and opening hours */}
                {/* For now keeping it simple with essential fields to finish Phase 2 Core */}

                <div className="form-actions">
                    <button type="submit" className="btn btn--primary">Save Practice Details</button>
                </div>
            </form>
        </div>
    );
};

export default MetadataForm;
